import { computed, readonly, ref } from 'vue';

const AUTH_STATUSES = Object.freeze({
    UNKNOWN: 'unknown',
    AUTHENTICATED: 'authenticated',
    LOGGED_OUT: 'logged-out'
});

const authStatus = ref(AUTH_STATUSES.UNKNOWN);
const csrfToken = ref('');
const loggingOut = ref(false);
const AUTHENTICATED_SESSION_KEY = 'corecry:auth:known-session';

const sessionStorageOrNull = () => {
    try {
        return typeof globalThis !== 'undefined' ? globalThis.sessionStorage : null;
    } catch {
        return null;
    }
};

export const hasKnownAuthenticatedSession = () => {
    return sessionStorageOrNull()?.getItem(AUTHENTICATED_SESSION_KEY) === '1';
};

const rememberAuthenticatedSession = () => {
    try {
        sessionStorageOrNull()?.setItem(AUTHENTICATED_SESSION_KEY, '1');
    } catch {
        // Session storage is only an optional reload optimization.
    }
};

const forgetAuthenticatedSession = () => {
    try {
        sessionStorageOrNull()?.removeItem(AUTHENTICATED_SESSION_KEY);
    } catch {
        // Session storage is only an optional reload optimization.
    }
};

const CSRF_COOKIE_NAMES = ['csrf_token', 'csrf-token', 'XSRF-TOKEN'];

const readCsrfTokenFromCookies = () => {
    if (typeof document === 'undefined' || !document.cookie) {
        return '';
    }

    return document.cookie
        .split(';')
        .map((item) => item.trim())
        .reduce((found, cookie) => {
            if (found) {
                return found;
            }

            const separatorIndex = cookie.indexOf('=');
            if (separatorIndex === -1) {
                return '';
            }

            const name = cookie.slice(0, separatorIndex).trim();
            if (!CSRF_COOKIE_NAMES.includes(name)) {
                return '';
            }

            return decodeURIComponent(cookie.slice(separatorIndex + 1).trim());
        }, '');
};

const refreshCsrfToken = () => {
    const token = readCsrfTokenFromCookies();
    csrfToken.value = token;
    return token;
};

const setCsrfToken = (token) => {
    csrfToken.value = typeof token === 'string' ? token : '';
};

// Attempt to hydrate CSRF token on module load (no-op during SSR)
refreshCsrfToken();

export function useAuthState() {
    const setStatus = (newStatus) => {
        if (!Object.values(AUTH_STATUSES).includes(newStatus)) {
            globalThis.clientLogger?.warn(`[AuthState] Attempted to set unknown status: ${newStatus}`);
            return;
        }
        if (newStatus !== authStatus.value) globalThis.clientLogger?.resetSession?.();
        authStatus.value = newStatus;
        if (newStatus === AUTH_STATUSES.AUTHENTICATED) rememberAuthenticatedSession();
        if (newStatus === AUTH_STATUSES.LOGGED_OUT) forgetAuthenticatedSession();
    };

    const resetStatus = () => {
        authStatus.value = AUTH_STATUSES.UNKNOWN;
    };

    const setLoggingOut = (value) => {
        loggingOut.value = Boolean(value);
    };

    return {
        status: readonly(authStatus),
        getStatus: () => authStatus.value,
        setStatus,
        resetStatus,
        loggingOut: readonly(loggingOut),
        getLoggingOutState: () => loggingOut.value,
        setLoggingOut,
        csrfToken: readonly(csrfToken),
        getCsrfToken: () => csrfToken.value,
        setCsrfToken,
        refreshCsrfToken,
        isAuthenticated: computed(() => authStatus.value === AUTH_STATUSES.AUTHENTICATED),
        isLoggedOut: computed(() => authStatus.value === AUTH_STATUSES.LOGGED_OUT),
        isUnknown: computed(() => authStatus.value === AUTH_STATUSES.UNKNOWN),
        AUTH_STATUSES
    };
}
